import { useEffect } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router";
import type { Role } from "@xinghai/shared";
import { EmptyState, Icon, Skeleton } from "@xinghai/ui";
import { useAuth } from "../features/auth/useAuth";
import { homeFor, ROLE_LABELS, useActiveRole } from "./roles";

function Loading() {
  return (
    <div className="xz-container xz-stack" aria-busy="true" aria-live="polite">
      <span className="xz-sr-only">正在加载…</span>
      <Skeleton variant="rect" height={72} />
      <Skeleton lines={3} />
    </div>
  );
}

/** Signed-in users only; sends everyone else to /login with a return path. */
export function RequireAuth() {
  const { me, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <Loading />;
  if (!me) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
}

const VIEW_STAFF: readonly Role[] = ["annotator", "adjudicator", "teacher", "course_team", "admin"];

/** Route guard for a role set. Server-side authorization is the real gate. */
export function RequireRole({ roles }: { roles: readonly Role[] }) {
  const { me, isLoading } = useAuth();
  const held = me?.user.roles ?? [];
  const [active, setActive] = useActiveRole(held);
  const match = held.find((r) => roles.includes(r));
  useEffect(() => {
    if (match && active && !roles.includes(active)) setActive(match);
  }, [active, match, roles, setActive]);
  if (isLoading) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  if (match) return <Outlet />;
  if (held.some((r) => VIEW_STAFF.includes(r))) {
    return (
      <>
        <p className="gz-notice xz-container" role="status">
          <Icon id="ui-info" size={20} />
          你正在只读查看。需要修改时，请换到有权限的身份。
        </p>
        <Outlet />
      </>
    );
  }
  return (
    <div className="xz-container">
      <EmptyState
        icon={<Icon id="ui-home" size={24} />}
        title="从你的首页继续"
        description="这个页面属于别的身份。下面是你可以直接去的地方。"
        action={
          <span className="xz-row">
            {held.map((r) => (
              <Link key={r} className="gz-button" to={homeFor(r)}>
                {ROLE_LABELS[r]}
              </Link>
            ))}
          </span>
        }
      />
    </div>
  );
}

/**
 * ACCOUNTS.md §3 — a temporary password unlocks nothing but the change-password
 * page. The server returns 403 for everything else; this keeps the UI honest.
 */
export function RequirePasswordChange() {
  const { me, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.must_change_password && location.pathname !== "/account/password") {
    return <Navigate to="/account/password" replace />;
  }
  return <Outlet />;
}

/**
 * 红线 14.6 — no task, no upload until all three consents are signed.
 * The server enforces it too; this only keeps the student out of a dead end.
 */
export function RequireConsent() {
  const { me, isLoading, consentRequired } = useAuth();
  if (isLoading) return <Loading />;
  if (!me) return <Navigate to="/login" replace />;
  if (consentRequired) return <Navigate to="/consent" replace />;
  return <Outlet />;
}
